import { useState, type ReactNode } from 'react'
import {
    ShieldAlert, ShieldCheck, ShieldQuestion, KeyRound, ServerCog,
    EyeOff, Eye, ChevronDown, ChevronRight, CircleAlert,
} from 'lucide-react'
import { MitreBadge, tacticForTechnique } from './MitreBadge'
import {
    adAudit, attackOf, recoveryActionFor,
    type AdCluster, type DetectionClass,
} from '@/lib/utils/ad-findings'
import { formatDateTime } from '@/lib/dateUtils'

/**
 * Active Directory findings.
 *
 * These get their own section, above the general findings list, for one reason: a compromised
 * domain controller in a backup re-compromises the whole domain when it is restored. An AD
 * finding is never "one server has an issue", so it must not render as one anonymous row among
 * several hundred.
 *
 * Colour comes from detection_class and never from severity — see the note in ad-findings.ts
 * for the ESC6 case that makes the distinction load-bearing.
 */

const CLASS_STYLE: Record<DetectionClass, { tone: string; chip: string; label: string; hint: string }> = {
    confirmed: {
        tone: 'text-red-600 dark:text-red-400',
        chip: 'bg-red-100 text-red-700 dark:bg-red-500/15 dark:text-red-300',
        label: 'Confirmed',
        hint: 'A recoverable credential or a proven artefact, not an inference.',
    },
    indicator: {
        tone: 'text-amber-600 dark:text-amber-400',
        chip: 'bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300',
        label: 'Indicator',
        hint: 'Evidence worth investigating. Not proof that an attack occurred.',
    },
    telemetry: {
        tone: 'text-muted-foreground',
        chip: 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300',
        label: 'Telemetry',
        hint: 'Recorded activity, kept for context.',
    },
    advisory: {
        tone: 'text-violet-700 dark:text-violet-300',
        chip: 'bg-violet-100 text-violet-800 dark:bg-violet-500/15 dark:text-violet-300',
        label: 'For review',
        hint: 'An interpretation or an observed change, not a detection. It does not change the status.',
    },
    posture: {
        tone: 'text-sky-700 dark:text-sky-300',
        chip: 'bg-sky-100 text-sky-800 dark:bg-sky-500/15 dark:text-sky-300',
        label: 'Posture',
        hint: 'A configuration weakness that predates any incident. Worth hardening; not a reason to hold a restore.',
    },
}

/** Friendly labels for the AD payload keys. Anything unrecognised still renders, by its key. */
const DETAIL_LABELS: Record<string, string> = {
    account: 'Account', accounts: 'Accounts', service: 'Service', object: 'Object',
    attribute: 'Attribute', timestamp: 'When', event_id: 'Event ID', ca: 'Certificate authority',
    edit_flags: 'Template flags', privilege: 'Privilege', principal: 'Granted to',
    command: 'Command', coverage_gap: 'Not analysed', recovered: 'Recovered credential',
}
// `attack` is the headline badge and `filepath` has its own row — neither belongs in the grid.
const DETAIL_SKIP = new Set(['attack', 'filepath', 'mitre_attack', 'mitre_technique'])

function fmtDetail(v: any): string {
    if (v == null) return '—'
    if (Array.isArray(v)) return v.join(', ')
    if (typeof v === 'object') return JSON.stringify(v)
    return String(v)
}

/**
 * A recovered credential is masked until asked for.
 *
 * This value is a working domain password. The dialog it sits in gets screenshotted into
 * tickets and shared in chat, and nothing about the finding requires the password to be on
 * screen by default — the operator needs to know WHICH credential is exposed, and can reveal
 * the value when they actually need it.
 */
function SecretValue({ value }: { value: string }) {
    const [shown, setShown] = useState(false)
    return (
        <span className="inline-flex items-center gap-1.5">
            <span className="font-mono text-xs break-all">{shown ? value : '•'.repeat(Math.min(12, Math.max(6, value.length)))}</span>
            <button
                type="button"
                onClick={() => setShown((s) => !s)}
                className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[11px] font-medium text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
                aria-label={shown ? 'Hide recovered credential' : 'Reveal recovered credential'}
            >
                {shown ? <EyeOff className="h-3 w-3" /> : <Eye className="h-3 w-3" />}
                {shown ? 'Hide' : 'Reveal'}
            </button>
        </span>
    )
}

function Banner({
    tone, Icon, title, children,
}: { tone: string; Icon: typeof ShieldAlert; title: string; children?: ReactNode }) {
    return (
        <div className={`flex items-start gap-3 rounded-lg border p-3 ${tone}`}>
            <Icon className="h-5 w-5 flex-shrink-0 mt-0.5" />
            <div className="min-w-0">
                <div className="text-sm font-semibold">{title}</div>
                {children && <div className="text-xs mt-0.5 leading-relaxed">{children}</div>}
            </div>
        </div>
    )
}

function AdFindingRow({ c }: { c: AdCluster }) {
    const [open, setOpen] = useState(false)
    const f = c.sample
    const cls = c.detectionClass
    const style = CLASS_STYLE[cls]
    const attack = attackOf(f)
    const tactic = tacticForTechnique(attack?.id)
    const action = recoveryActionFor(f)
    const details = Object.entries(f.details || {}).filter(([k, v]) => !DETAIL_SKIP.has(k) && v != null)

    return (
        <div className="border rounded-lg overflow-hidden">
            <button
                type="button"
                onClick={() => setOpen((o) => !o)}
                aria-expanded={open}
                className="w-full flex items-center gap-2.5 px-3 py-2.5 text-left hover:bg-muted/50 transition-colors"
            >
                {open ? <ChevronDown className="h-4 w-4 flex-shrink-0 text-muted-foreground" /> : <ChevronRight className="h-4 w-4 flex-shrink-0 text-muted-foreground" />}
                {/* The technique leads. It is the most useful field an operator has here. */}
                {attack && <span className="flex-shrink-0"><MitreBadge id={attack.id} name={attack.name} compact /></span>}
                {/* The count replaces N identical rows. Its LENGTH is what a reader reads as
                    severity, so one observation must look like one thing. */}
                {c.count > 1 && (
                    <span className={`flex-shrink-0 text-sm font-bold tabular-nums ${style.tone}`} title={`${c.count} recordings of the same observation`}>
                        {c.count}&times;
                    </span>
                )}
                <span className="text-sm font-medium flex-1 min-w-0 truncate">{c.title}</span>
                <span className="flex-shrink-0 hidden md:inline text-[11px] text-muted-foreground truncate max-w-[160px]">
                    {c.account || 'unattributed'}
                </span>
                {action && (
                    <span className="flex-shrink-0 hidden lg:inline-flex items-center gap-1 rounded px-2 py-0.5 text-[11px] font-medium bg-blue-100 text-blue-700 dark:bg-blue-500/15 dark:text-blue-300">
                        <KeyRound className="h-3 w-3" />Action before recovery
                    </span>
                )}
                <span className={`flex-shrink-0 inline-flex items-center rounded px-2 py-0.5 text-xs font-medium ${style.chip}`} title={style.hint}>
                    {style.label}
                </span>
            </button>

            {open && (
                <div className="border-t bg-muted/30 px-4 py-3 space-y-3">
                    {/* Written for a human by the scanner — rendered verbatim. */}
                    {f.description && <p className="text-sm leading-relaxed">{f.description}</p>}

                    {c.count > 1 && (
                        <div className="text-xs text-muted-foreground">
                            Recorded <span className="font-semibold tabular-nums">{c.count}</span> times
                            {c.first && c.last && (
                                c.first.getTime() === c.last.getTime()
                                    ? <> at {formatDateTime(c.first)}</>
                                    : <> between {formatDateTime(c.first)} and {formatDateTime(c.last)}</>
                            )}
                            . Collapsed because every recording is identical apart from the time.
                        </div>
                    )}

                    <div className="text-xs">
                        <span className="text-muted-foreground">Account </span>
                        {c.account
                            ? <span className="font-mono">{c.account}</span>
                            : <span className="italic text-muted-foreground" title="The engine could not identify the subject of this event.">unattributed</span>}
                    </div>

                    <div className="flex flex-wrap items-center gap-2">
                        {attack && <MitreBadge id={attack.id} name={attack.name} />}
                        {tactic && <span className="inline-flex items-center rounded px-2 py-0.5 text-xs font-medium bg-muted text-muted-foreground">{tactic}</span>}
                        {/* Severity is shown, but as a secondary impact note — never as the colour. */}
                        {f.severity && (
                            <span className="inline-flex items-center rounded px-2 py-0.5 text-xs font-medium bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300"
                                  title="Impact if this turns out to be real. It does not decide the classification.">
                                Impact if real: {String(f.severity)}
                            </span>
                        )}
                        {f.confidence && (
                            <span className="inline-flex items-center rounded px-2 py-0.5 text-xs font-medium bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300">
                                {String(f.confidence)} confidence
                            </span>
                        )}
                    </div>

                    {details.length > 0 && (
                        <dl className="grid grid-cols-[minmax(0,9rem)_minmax(0,1fr)] gap-x-3 gap-y-1.5 text-xs">
                            {details.map(([k, v]) => (
                                <div key={k} className="contents">
                                    <dt className="text-muted-foreground truncate">{DETAIL_LABELS[k] || k}</dt>
                                    <dd className="min-w-0 break-words">
                                        {k === 'recovered' ? <SecretValue value={fmtDetail(v)} /> : <span className="font-mono">{fmtDetail(v)}</span>}
                                    </dd>
                                </div>
                            ))}
                        </dl>
                    )}

                    {f.filepath && (
                        <div className="text-xs">
                            <span className="text-muted-foreground">Path </span>
                            <span className="font-mono break-all">{f.filepath}</span>
                        </div>
                    )}

                    {action && (
                        <div className="flex items-start gap-2 rounded-md border border-blue-300 bg-blue-50 dark:border-blue-800/60 dark:bg-blue-950/30 p-2.5">
                            <KeyRound className="h-4 w-4 flex-shrink-0 mt-0.5 text-blue-600 dark:text-blue-400" />
                            <div className="text-xs leading-relaxed">
                                <span className="font-semibold">Before restoring this object. </span>{action}
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    )
}

export function ADFindingsSection({ report }: { report: any }) {
    const audit = adAudit(report)

    // The engine ran and correctly stood down. Say that, and nothing that sounds like a result.
    if (audit.state === 'not-dc') {
        return (
            <Banner
                tone="border-slate-300 bg-slate-50 dark:border-slate-700 dark:bg-slate-900/40 text-slate-700 dark:text-slate-300"
                Icon={ServerCog}
                title="Not a domain controller"
            >
                The Active Directory audit does not apply to this object.
            </Banner>
        )
    }

    // A domain controller with a genuinely clean audit. This one may be said positively.
    if (audit.state === 'clean') {
        return (
            <Banner
                tone="border-emerald-300 bg-emerald-50 dark:border-emerald-800/60 dark:bg-emerald-950/30 text-emerald-800 dark:text-emerald-300"
                Icon={ShieldCheck}
                title="Active Directory audit completed — nothing found"
            >
                Every Active Directory check ran against this domain controller and none reported a finding.
            </Banner>
        )
    }

    // Nothing in the report mentions the engine either way. NOT the same as clean, and not the
    // same as "not a domain controller" — both of those would be reassurance we cannot support.
    if (audit.state === 'not-reported') {
        return (
            <Banner
                tone="border-slate-300 bg-slate-50 dark:border-slate-700 dark:bg-slate-900/40 text-slate-700 dark:text-slate-300"
                Icon={ShieldQuestion}
                title="No Active Directory audit in this report"
            >
                This scan predates the Active Directory engine, or the engine did not report. Whether this
                object is a domain controller cannot be told from this report — treat it as unassessed
                rather than clear.
            </Banner>
        )
    }

    const { counts, groups, coverageGap } = audit
    const anyConfirmed = counts.confirmed > 0
    // The banner takes the colour of the worst class present. Posture and telemetry never
    // escalate, so a domain controller whose only findings are hardening items is not amber.
    const tone = anyConfirmed ? 'red' : counts.indicator > 0 ? 'amber' : 'neutral'

    return (
        <div className="space-y-3">
            <div className={`rounded-lg border p-3 ${tone === 'red'
                ? 'border-red-300 bg-red-50 dark:border-red-800/60 dark:bg-red-950/30'
                : tone === 'amber'
                    ? 'border-amber-300 bg-amber-50 dark:border-amber-800/60 dark:bg-amber-950/30'
                    : 'border-slate-300 bg-slate-50 dark:border-slate-700 dark:bg-slate-900/40'}`}>
                <div className="flex items-start gap-3">
                    <ShieldAlert className={`h-5 w-5 flex-shrink-0 mt-0.5 ${tone === 'red' ? 'text-red-600 dark:text-red-400' : tone === 'amber' ? 'text-amber-600 dark:text-amber-400' : 'text-slate-500 dark:text-slate-400'}`} />
                    <div className="min-w-0 flex-1">
                        <div className="text-sm font-semibold">Active Directory — findings are domain-wide</div>
                        <div className="text-xs mt-0.5 leading-relaxed">
                            This object is a domain controller. Restoring it restores whatever is described below
                            along with it, for every account and machine in the domain — these are not findings
                            about one server.
                        </div>
                        <div className="flex flex-wrap items-center gap-3 mt-2">
                            {/* The AD engine does not produce advisory findings; its count shows only if one arrives. */}
                            {(['confirmed', 'indicator', 'telemetry', 'advisory', 'posture'] as DetectionClass[]).filter((c) => c !== 'advisory' || counts.advisory > 0).map((c) => (
                                <span key={c} className="inline-flex items-baseline gap-1.5" title={CLASS_STYLE[c].hint}>
                                    <span className={`text-lg font-bold tabular-nums leading-none ${counts[c] > 0 ? CLASS_STYLE[c].tone : 'text-muted-foreground/50'}`}>{counts[c]}</span>
                                    <span className="text-[11px] text-muted-foreground">{CLASS_STYLE[c].label.toLowerCase()}</span>
                                </span>
                            ))}
                        </div>
                    </div>
                </div>
            </div>

            {/* An explicit gap. The scanner is deliberately honest about what it could not read,
                and this panel exists so the UI does not quietly undo that by implying clean. */}
            {coverageGap && (
                <Banner
                    tone="border-slate-400 bg-slate-100 dark:border-slate-600 dark:bg-slate-800/50 text-slate-800 dark:text-slate-200"
                    Icon={CircleAlert}
                    title={coverageGap.title || 'Active Directory database not fully analysed'}
                >
                    {coverageGap.description
                        || 'Some directory-database checks did not run, so the absence of a finding from them is not evidence of absence.'}
                </Banner>
            )}

            {groups.map((g) => (
                <div key={g.source}>
                    <div className="flex items-baseline gap-2 mb-1.5">
                        <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{g.label}</h4>
                        <span className="text-[11px] text-muted-foreground/80">{g.blurb}</span>
                        <span className="text-[11px] text-muted-foreground tabular-nums ml-auto">
                            {g.clusters.length === g.findingCount
                                ? g.findingCount
                                : `${g.clusters.length} of ${g.findingCount}`}
                        </span>
                    </div>
                    <div className="space-y-1.5">
                        {g.clusters.map((c) => <AdFindingRow key={c.key} c={c} />)}
                    </div>
                </div>
            ))}
        </div>
    )
}
